import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI, generateSessionId } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from './AuthContext';
import { trackAddToCart } from '../utils/analytics';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();

  // Get the identifier to use for cart (user_id if logged in, session_id otherwise)
  const getCartIdentifier = useCallback(() => {
    return isAuthenticated && user?.user_id ? user.user_id : sessionId;
  }, [isAuthenticated, user, sessionId]);

  // Load cart on mount and when auth changes
  const loadCart = useCallback(async () => {
    try {
      setLoading(true);
      const identifier = getCartIdentifier();
      const items = await cartAPI.getCart(identifier);
      setCartItems(items);
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  }, [getCartIdentifier]);

  // Merge session cart with user cart on login
  const mergeCart = useCallback(async () => {
    if (!isAuthenticated || !user?.user_id) return;
    
    try {
      await cartAPI.mergeCart(sessionId);
      await loadCart();
    } catch (error) {
      console.error('Failed to merge cart:', error);
    }
  }, [isAuthenticated, user, sessionId, loadCart]);

  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge cart when user logs in
  useEffect(() => {
    if (isAuthenticated && user?.user_id) {
      mergeCart();
    }
  }, [isAuthenticated, user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = async (product, quantity = 1) => {
    try {
      setLoading(true);
      
      // Get the price - use selectedDosage price or base price
      const price = product.price || 0;
      const selectedVariant = product.selectedDosage || null;
      
      const item = {
        product_id: product.id,
        quantity,
        price: price,
        selected_variant: selectedVariant
      };
      
      // Use user_id if logged in, session_id otherwise
      if (isAuthenticated && user?.user_id) {
        item.user_id = user.user_id;
      } else {
        item.session_id = sessionId;
      }
      
      await cartAPI.addItem(item);
      await loadCart();

      // GA4 add_to_cart + Yandex.Metrica ecommerce.add + reachGoal('add_to_cart').
      // Pillar of the Google Ads Performance Max + DV360 conversion funnel.
      try {
        trackAddToCart(product, quantity);
      } catch (_) { /* analytics never blocks UX */ }

      const productName = product.name?.en || product.name?.es || 'Product';
      const variantText = selectedVariant ? ` (${selectedVariant})` : '';
      toast({
        title: "Added to cart",
        description: `${productName}${variantText} has been added to your cart.`,
      });
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      setLoading(true);
      await cartAPI.updateItem(itemId, { quantity });
      await loadCart();
    } catch (error) {
      console.error('Failed to update cart item:', error);
      toast({
        title: "Error",
        description: "Failed to update cart item. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      await cartAPI.removeItem(itemId);
      await loadCart();
      
      toast({
        title: "Removed from cart",
        description: "Item has been removed from your cart.",
      });
    } catch (error) {
      console.error('Failed to remove from cart:', error);
      toast({
        title: "Error",
        description: "Failed to remove item from cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      const identifier = getCartIdentifier();
      await cartAPI.clearCart(identifier);
      setCartItems([]);
      
      toast({
        title: "Cart cleared",
        description: "All items have been removed from your cart.",
      });
    } catch (error) {
      console.error('Failed to clear cart:', error);
      toast({
        title: "Error",
        description: "Failed to clear cart. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      // Use item.price (stored at add time) or fallback to product price
      const price = item.price || item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const updateQuantity = async (itemId, quantity) => {
    await updateCartItem(itemId, quantity);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      loading,
      sessionId,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      getCartCount,
      getCartTotal,
      loadCart,
      updateQuantity
    }}>
      {children}
    </CartContext.Provider>
  );
};